export interface UserProfile {
    /** Effective app user id. May be a canonical profile id rather than Firebase Auth uid. */
    uid: string;
    /** Actual Firebase Auth uid for the signed-in credential. */
    authUid?: string;
    /** Canonical app user id resolved from the auth profile. */
    canonicalUserId?: string;
    /** Email on the signed-in Firebase Auth credential, kept separate from the app profile email. */
    authEmail?: string;
    email: string;
    displayName: string;
    photoURL?: string;
    isSuperAdmin?: boolean; // Global admin access (billing, multi-project management)
    activeOrgId?: string; // UI-selected workspace scope, not canonical identity
    needsSetup?: boolean; // First-time setup wizard required
    calendarFeedTokens?: Record<string, string>; // Per-project opaque calendar subscription tokens
    calendarTokens?: Record<string, string>; // Legacy path-scoped calendar subscription tokens
    /** Project/member scoped collaborator welcome card dismissals */
    projectWelcomeDismissals?: Record<string, boolean>;
}

/**
 * Granular permissions for RBAC system
 */
export const Permission = {
    // Project access permissions
    VIEW_PROJECT: 'VIEW_PROJECT',
    EDIT_OWN_PROFILE: 'EDIT_OWN_PROFILE',
    EDIT_PERSONNEL_OPERATIONAL: 'EDIT_PERSONNEL_OPERATIONAL',
    EDIT_CASTING_ASSIGNMENTS: 'EDIT_CASTING_ASSIGNMENTS',
    MANAGE_MEMBERS: 'MANAGE_MEMBERS',
    MANAGE_PROJECT_ADMINS: 'MANAGE_PROJECT_ADMINS',
    MANAGE_PROJECT_OWNER: 'MANAGE_PROJECT_OWNER',

    // View permissions
    VIEW_ALL: 'VIEW_ALL',
    VIEW_ACTION_ITEMS: 'VIEW_ACTION_ITEMS',
    
    // Edit permissions
    EDIT_RUNSHEET: 'EDIT_RUNSHEET',
    EDIT_PROPS: 'EDIT_PROPS',
    EDIT_COSTUMES: 'EDIT_COSTUMES',
    EDIT_SETS: 'EDIT_SETS',
    EDIT_BLOCKING: 'EDIT_BLOCKING',
    EDIT_SCHEDULER: 'EDIT_SCHEDULER',
    EDIT_CAST: 'EDIT_CAST',
    EDIT_NOTES: 'EDIT_NOTES',
    EDIT_ACTION_ITEMS: 'EDIT_ACTION_ITEMS',
    EDIT_SOUND: 'EDIT_SOUND',
    EDIT_LIGHTING: 'EDIT_LIGHTING',
    EDIT_PROJECTIONS: 'EDIT_PROJECTIONS',
    VIEW_PROMPTBOOK: 'VIEW_PROMPTBOOK',
    EDIT_PROMPTBOOK: 'EDIT_PROMPTBOOK',
    EDIT_FILES: 'EDIT_FILES',
    CREATE_FILES: 'CREATE_FILES',
    MANAGE_FILE_ACCESS: 'MANAGE_FILE_ACCESS',
    
    // Management permissions
    MANAGE_FEED: 'MANAGE_FEED',
    SEND_EMAIL: 'SEND_EMAIL',
    MANAGE_COMMUNICATIONS: 'MANAGE_COMMUNICATIONS',
    MANAGE_USERS: 'MANAGE_USERS',
    MANAGE_PROJECT: 'MANAGE_PROJECT',
    DELETE_PROJECT: 'DELETE_PROJECT',
    
    // Budget permissions
    VIEW_BUDGET: 'VIEW_BUDGET',
    EDIT_BUDGET: 'EDIT_BUDGET',
    
    // Production Management permissions
    VIEW_PRODUCTION: 'VIEW_PRODUCTION',
    EDIT_PRODUCTION: 'EDIT_PRODUCTION',
    
    // Special permissions
    SELF_CHECK_IN: 'SELF_CHECK_IN',
    UPDATE_OWN_PROFILE: 'UPDATE_OWN_PROFILE',

    // Performance permissions
    CALL_SHOW: 'CALL_SHOW',
} as const;

export type PermissionValue = (typeof Permission)[keyof typeof Permission];

/**
 * Role definition with granular permissions
 */
export interface Role {
    id: string;
    name: string;
    permissions: PermissionValue[];
    isBuiltIn: boolean; // True for default roles, false for custom
    description?: string;
    sourceTemplateId?: string;
    customized?: boolean;
}

/**
 * Project member with role assignment
 */
export interface ProjectMember {
    userId: string;
    roleId: string;
    personnelId?: string; // Canonical link to Personnel.id
    status: 'active' | 'pending' | 'inactive' | 'removed';
    joinedAt: number | string | Date;
    updatedAt?: number | string | Date;
    invitationId?: string;
    invitedBy?: string;
    /** SM wizard dismissed for this project */
    onboardingDismissed?: boolean;
}











